import { ConfirmationModal } from "@shared/components/ConfirmationModal";
import { Editor } from "@shared/components/Editor";
import { InputControl } from "@shared/components/form";
import { MediaUploader } from "@shared/components/media/MediaUploader";
import { Button } from "@shared/components/ui/Button";
import { MediaFile, MediaType } from "@shared/types/media.types";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { MediaItem } from "../../spaces/types/space.types";
import {
  CreateResourceData,
  Resource,
  ResourceStatus,
  UpdateResourceData,
} from "../types/resource.types";
import MediaPreview from "./MediaPreview";

interface ResourceFormProps {
  resource?: Resource;
  onSubmit: (data: CreateResourceData | UpdateResourceData) => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

const ResourceForm: React.FC<ResourceFormProps> = ({
  resource,
  onSubmit,
  onDelete,
  onCancel,
  isSubmitting,
  canEdit = true,
  canDelete = false,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Featured image state
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [showMediaUploader, setShowMediaUploader] = useState(false);

  // Materials state
  const [materials, setMaterials] = useState<
    {
      id?: string;
      title: string;
      category: string;
      status: ResourceStatus;
      resourceUrl?: string;
      image?: MediaFile[];
      resourceFile?: MediaFile[];
      showImageUploader: boolean;
      showFileUploader: boolean;
      isExpanded: boolean;
      resourceType: "url" | "file";
    }[]
  >([]);

  // Initialize form with resource data if editing
  useEffect(() => {
    if (resource) {
      setTitle(resource.title);
      setDescription(resource.description || "");

      // Initialize featured image if it exists
      setMediaFiles(
        resource.featuredImage
          ? mediaItemsToMediaFiles(resource.featuredImage)
          : [],
      );

      // Initialize materials if they exist
      if (resource.materials && resource.materials.length > 0) {
        setMaterials(
          resource.materials.map((material) => ({
            id: material.id,
            title: material.title,
            category: material.category,
            status: material.status,
            resourceUrl: material.resourceUrl,
            image: material.image ? mediaItemsToMediaFiles(material.image) : [],
            resourceFile: material.resourceFile
              ? mediaItemsToMediaFiles(material.resourceFile)
              : [],
            showImageUploader: false,
            showFileUploader: false,
            isExpanded: false, // Collapsed by default in edit mode
            resourceType: material.resourceUrl ? "url" : "file",
          })),
        );
      } else {
        // Add an empty material if none exist
        addEmptyMaterial();
      }
    } else {
      // Initialize with empty material for new resource (just one)
      addEmptyMaterial();
    }
  }, [resource]);

  const addEmptyMaterial = () => {
    setMaterials((prev) => [
      ...prev,
      {
        title: "",
        category: "",
        status: ResourceStatus.READY,
        resourceUrl: "",
        image: [],
        resourceFile: [],
        showImageUploader: false,
        showFileUploader: false,
        isExpanded: true,
        resourceType: "url", // 'url' or 'file'
      },
    ]);
  };

  const removeMaterial = (index: number) => {
    setMaterials((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMaterial = (index: number, field: string, value: any) => {
    setMaterials((prev) =>
      prev.map((material, i) =>
        i === index ? { ...material, [field]: value } : material,
      ),
    );
  };

  const handleMediaUpload = (newMediaFiles: MediaFile[]) => {
    setMediaFiles([...mediaFiles, ...newMediaFiles]);
    setShowMediaUploader(false);
  };

  const handleMaterialImageUpload = (
    index: number,
    newMediaFiles: MediaFile[],
  ) => {
    updateMaterial(index, "image", [
      ...(materials[index].image || []),
      ...newMediaFiles,
    ]);
    updateMaterial(index, "showImageUploader", false);
  };

  const handleMaterialFileUpload = (
    index: number,
    newMediaFiles: MediaFile[],
  ) => {
    updateMaterial(index, "resourceFile", [
      ...(materials[index].resourceFile || []),
      ...newMediaFiles,
    ]);
    updateMaterial(index, "showFileUploader", false);
  };

  const mediaItemsToMediaFiles = (mediaItems: MediaItem[]): MediaFile[] => {
    return mediaItems.map((item) => ({
      type: item.type as MediaType,
      preview: item.variants[0]?.url || "",
      uploadedUrl: item.variants[0]?.url || "",
      file: new File([], "", { type: `${item.type}/jpeg` }),
      progress: 100,
    }));
  };

  // Convert MediaFile to MediaItem for API
  const convertMediaFilesToItems = (files: MediaFile[]): MediaItem[] => {
    return files.map((file) => {
      // Convert MediaType enum to string type expected by MediaItem
      let mediaItemType: "image" | "video" | "document";

      switch (file.type) {
        case MediaType.IMAGE:
          mediaItemType = "image";
          break;
        case MediaType.VIDEO:
          mediaItemType = "video";
          break;
        case MediaType.DOCUMENT:
          mediaItemType = "document";
          break;
        default:
          mediaItemType = "image"; // Default fallback
      }

      return {
        type: mediaItemType,
        variants: [
          {
            url: file.uploadedUrl || file.preview,
            width: 0,
            height: 0,
            format: file.type === MediaType.DOCUMENT ? "pdf" : "jpeg",
            type: "original",
          },
        ],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }

    // Validate featured image
    if (mediaFiles.length === 0) {
      toast.error("Featured image is required");
      return;
    }

    // Validate that all materials have title
    const materialsWithoutTitle = materials.filter((m) => !m.title.trim());
    if (materialsWithoutTitle.length > 0) {
      toast.error(
        `All resources must have a title (Resource ${
          materials.indexOf(materialsWithoutTitle[0]) + 1
        } is missing a title)`,
      );
      return;
    }

    // Validate that all materials have image
    const materialsWithoutImage = materials.filter((m) => m.image.length === 0);
    if (materialsWithoutImage.length > 0) {
      toast.error(
        `All resources must have at least one image (Resource ${
          materials.indexOf(materialsWithoutImage[0]) + 1
        } is missing an image)`,
      );
      return;
    }

    try {
      // Prepare data for submission
      const resourceData: CreateResourceData | UpdateResourceData = {
        title,
        description,
        featuredImage:
          mediaFiles.length > 0
            ? convertMediaFilesToItems(mediaFiles)
            : undefined,
        materials: materials.map((material) => ({
          id: material.id,
          title: material.title,
          category: material.category,
          status: material.status,
          resourceUrl: material.resourceUrl?.trim() || undefined,
          image:
            material.image && material.image.length > 0
              ? convertMediaFilesToItems(material.image)
              : undefined,
          resourceFile:
            material.resourceFile && material.resourceFile.length > 0
              ? convertMediaFilesToItems(material.resourceFile)
              : undefined,
        })),
      };

      await onSubmit(resourceData);
      if (resource) {
        toast.success("Resource updated successfully");
      }
    } catch (err) {
      console.error("Error submitting resource:", err);
      toast.error("Failed to save resource. Please try again.");
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete?.();
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
      toast.success("Resource deleted successfully");
    } catch (err) {
      console.error("Error deleting resource:", err);
      setIsDeleting(false);
      toast.error("Failed to delete resource");
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 w-full">
      <div className="p-3">
        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
          {resource ? "Edit Resource" : "Create Resource"}
        </h2>
        <div>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Title */}
              <InputControl
                label="Title"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter resource title"
                required
                disabled={!canEdit || isSubmitting}
              />

              {/* Featured Image - Moved to the top */}
              <div className="mt-[20px] mb-4">
                <div className="flex items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Featured Image <span className="text-red-500 ml-1">*</span>
                  </label>
                </div>

                {/* Media Uploader if no image */}
                {mediaFiles.length === 0 && (
                  <div className="mb-3">
                    <MediaUploader
                      onUploadComplete={handleMediaUpload}
                      onError={(error) => toast.error(error)}
                      maxFiles={1}
                      acceptType="image"
                    />
                  </div>
                )}

                {/* Media preview */}
                {mediaFiles.length > 0 && (
                  <MediaPreview
                    files={mediaFiles}
                    onRemove={() => setMediaFiles([])}
                    disabled={!canEdit || isSubmitting}
                  />
                )}
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Description
                </label>
                <Editor
                  initialValue={description}
                  onChange={setDescription}
                  placeholder="Enter resource description"
                  disabled={!canEdit || isSubmitting}
                />
              </div>

              {/* Materials Section */}
              <div className="mt-6">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white">
                    Resources
                  </h3>
                  <Button
                    type="button"
                    onClick={addEmptyMaterial}
                    disabled={!canEdit || isSubmitting}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm py-1"
                  >
                    Add Resource
                  </Button>
                </div>

                {materials.map((material, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 dark:border-gray-700 rounded-md p-4 mb-4"
                  >
                    <div
                      className="flex justify-between items-center cursor-pointer"
                      onClick={() =>
                        updateMaterial(
                          index,
                          "isExpanded",
                          !material.isExpanded,
                        )
                      }
                    >
                      <h4 className="font-medium text-gray-800 dark:text-white">
                        {material.title
                          ? material.title
                          : `Resource ${index + 1}`}
                      </h4>
                      <div className="flex items-center space-x-2">
                        {materials.length > 1 && index > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeMaterial(index);
                            }}
                            disabled={!canEdit || isSubmitting}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-gray-500 hover:text-gray-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            updateMaterial(
                              index,
                              "isExpanded",
                              !material.isExpanded,
                            );
                          }}
                        >
                          <svg
                            className={`h-5 w-5 transform transition-transform ${
                              material.isExpanded ? "rotate-180" : ""
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {material.isExpanded && (
                      <div className="space-y-3 mt-3">
                        {/* Material Image */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Featured Image{" "}
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          {material.image && material.image.length > 0 ? (
                            <MediaPreview
                              files={material.image}
                              onRemove={(fileIndex) => {
                                updateMaterial(
                                  index,
                                  "image",
                                  material.image?.filter(
                                    (_, i) => i !== fileIndex,
                                  ) || [],
                                );
                              }}
                              disabled={!canEdit || isSubmitting}
                            />
                          ) : (
                            <div className="mb-3">
                              <MediaUploader
                                onUploadComplete={(files) => {
                                  updateMaterial(index, "image", [
                                    ...material.image,
                                    ...files,
                                  ]);
                                }}
                                onError={(error) => toast.error(error)}
                                maxFiles={1}
                                acceptType="image"
                              />
                            </div>
                          )}
                        </div>
                        {/* Material Title */}
                        <InputControl
                          label="Title"
                          id={`material-title-${index}`}
                          value={material.title}
                          onChange={(e) =>
                            updateMaterial(index, "title", e.target.value)
                          }
                          placeholder="Enter resource title"
                          disabled={!canEdit || isSubmitting}
                          required
                        />

                        {/* Material Category */}
                        <InputControl
                          label="Category"
                          id={`material-category-${index}`}
                          value={material.category}
                          onChange={(e) =>
                            updateMaterial(index, "category", e.target.value)
                          }
                          placeholder="Enter resource category"
                          disabled={!canEdit || isSubmitting}
                          required
                        />

                        {/* Material Status */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Status
                          </label>
                          <select
                            value={material.status}
                            onChange={(e) =>
                              updateMaterial(index, "status", e.target.value)
                            }
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            disabled={!canEdit || isSubmitting}
                          >
                            {Object.values(ResourceStatus).map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Resource Type Selector */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Resource Type
                          </label>
                          <div className="flex space-x-4">
                            <div className="flex items-center">
                              <input
                                id={`url-type-${index}`}
                                type="radio"
                                name={`resource-type-${index}`}
                                value="url"
                                checked={material.resourceType === "url"}
                                onChange={() =>
                                  updateMaterial(index, "resourceType", "url")
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                disabled={!canEdit || isSubmitting}
                              />
                              <label
                                htmlFor={`url-type-${index}`}
                                className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                              >
                                URL
                              </label>
                            </div>
                            <div className="flex items-center">
                              <input
                                id={`file-type-${index}`}
                                type="radio"
                                name={`resource-type-${index}`}
                                value="file"
                                checked={material.resourceType === "file"}
                                onChange={() =>
                                  updateMaterial(index, "resourceType", "file")
                                }
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                disabled={!canEdit || isSubmitting}
                              />
                              <label
                                htmlFor={`file-type-${index}`}
                                className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                              >
                                File
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Material URL or File based on resourceType */}
                        {material.resourceType === "url" ? (
                          <InputControl
                            label="URL"
                            id={`material-url-${index}`}
                            value={material.resourceUrl || ""}
                            onChange={(e) =>
                              updateMaterial(
                                index,
                                "resourceUrl",
                                e.target.value,
                              )
                            }
                            placeholder="Enter material URL"
                            disabled={!canEdit || isSubmitting}
                          />
                        ) : (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              File
                            </label>
                            {material.resourceFile &&
                            material.resourceFile.length > 0 ? (
                              <div className="grid grid-cols-1 mb-2">
                                {material.resourceFile.map(
                                  (file, fileIndex) => (
                                    <div
                                      key={fileIndex}
                                      className="relative rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700 p-2 w-full"
                                    >
                                      <div className="flex items-center">
                                        <svg
                                          className="h-8 w-8 text-gray-400"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                          />
                                        </svg>
                                        <span className="ml-2 text-sm truncate">
                                          {file.file.name || "File"}
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        className="absolute top-1 right-1 bg-gray-800 bg-opacity-60 rounded-full p-1 text-white"
                                        onClick={() => {
                                          updateMaterial(
                                            index,
                                            "resourceFile",
                                            material.resourceFile?.filter(
                                              (_, i) => i !== fileIndex,
                                            ) || [],
                                          );
                                        }}
                                        disabled={!canEdit || isSubmitting}
                                      >
                                        <svg
                                          className="h-4 w-4"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                          />
                                        </svg>
                                      </button>
                                      {file.progress < 100 && (
                                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
                                          <div
                                            className={`h-full ${theme.bg.brand}`}
                                            style={{
                                              width: `${file.progress}%`,
                                            }}
                                          ></div>
                                        </div>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() =>
                                  updateMaterial(
                                    index,
                                    "showFileUploader",
                                    true,
                                  )
                                }
                                disabled={!canEdit || isSubmitting}
                              >
                                Upload File
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {error && (
                  <div className="text-red-500 text-sm mt-2">{error}</div>
                )}

                <div className="flex justify-between  space-x-2">
                  <div>
                    {resource && onDelete && canDelete && (
                      <Button
                        type="button"
                        onClick={handleDelete}
                        variant="error"
                        disabled={isSubmitting}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      onClick={onCancel}
                      disabled={isSubmitting}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting || !canEdit}>
                      {isSubmitting
                        ? "Saving..."
                        : resource
                          ? "Update"
                          : "Create"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Media Uploaders */}
      {showMediaUploader && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Upload Media</h3>
              <button
                type="button"
                onClick={() => setShowMediaUploader(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <MediaUploader
              onUploadComplete={handleMediaUpload}
              onError={(error) => setError(error)}
            />
          </div>
        </div>
      )}

      {materials.map((material, index) => (
        <React.Fragment key={`uploader-${index}`}>
          {/* Image Uploader */}
          {material.showImageUploader && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">Upload Image</h3>
                  <button
                    type="button"
                    onClick={() =>
                      updateMaterial(index, "showImageUploader", false)
                    }
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <MediaUploader
                  onUploadComplete={(files) =>
                    handleMaterialImageUpload(index, files)
                  }
                  onError={(error) => setError(error)}
                />
              </div>
            </div>
          )}

          {/* File Uploader */}
          {material.showFileUploader && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-lg">
                <h3 className="text-lg font-medium mb-4 text-gray-800 dark:text-white">
                  Upload File
                </h3>
                <MediaUploader
                  onUploadComplete={(files) =>
                    handleMaterialFileUpload(index, files)
                  }
                  onError={(error) => setError(error)}
                  maxFiles={1}
                  acceptType="document"
                />
                <div className="flex justify-end mt-4">
                  <Button
                    type="button"
                    onClick={() =>
                      updateMaterial(index, "showFileUploader", false)
                    }
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </React.Fragment>
      ))}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="Delete Resource"
        message="Are you sure you want to delete this resource? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ResourceForm;
